import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Globe, TrendingUp, TrendingDown, ArrowRight, Building2, Zap, AlertTriangle, Download, ExternalLink } from 'lucide-react';

const ct = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';

// STATIC MACRO DATA — Sources: ECB, CBS, Kadaster, CPB, DNB, CBRE, JLL
const MACRO = {
  ecbRate: { v: 2.65, prev: 2.90, avg12: 3.40, trend: 'down', src: 'ECB' },
  swap10y: { v: 2.48, prev: 2.55, avg12: 2.72, trend: 'down', src: 'ECB/Reuters' },
  mortgage: { v: 3.85, prev: 3.95, avg12: 4.15, trend: 'down', src: 'DNB/Hypotheekshop' },
  hpi: { v: 6.2, prev: 5.8, avg12: 4.1, trend: 'up', src: 'CBS/Kadaster' },
  rentGrowth: { v: 4.8, prev: 4.5, avg12: 3.9, trend: 'up', src: 'CBS/Pararius' },
  txVolume: { v: -12, prev: -18, avg12: -15, trend: 'up', src: 'Kadaster' },
  yieldRes: { v: 4.2, prev: 4.3, avg12: 4.5, trend: 'down', src: 'CBRE/JLL' },
  yieldOff: { v: 5.8, prev: 5.9, avg12: 6.1, trend: 'down', src: 'CBRE/JLL' },
  inflation: { v: 2.4, prev: 2.7, avg12: 3.1, trend: 'down', src: 'CBS' },
  gdp: { v: 1.8, prev: 1.5, avg12: 1.2, trend: 'up', src: 'CPB' },
};

const ECB_HIST = [
  { date: '2021-Q1', rate: 0.0, swap: 0.1, mortgage: 1.5 }, { date: '2021-Q3', rate: 0.0, swap: 0.2, mortgage: 1.4 },
  { date: '2022-Q1', rate: 0.0, swap: 0.8, mortgage: 2.0 }, { date: '2022-Q3', rate: 1.25, swap: 2.3, mortgage: 3.8 },
  { date: '2023-Q1', rate: 3.0, swap: 3.1, mortgage: 4.3 }, { date: '2023-Q3', rate: 4.0, swap: 3.0, mortgage: 4.5 },
  { date: '2024-Q1', rate: 4.0, swap: 2.8, mortgage: 4.2 }, { date: '2024-Q3', rate: 3.5, swap: 2.6, mortgage: 4.0 },
  { date: '2025-Q1', rate: 2.9, swap: 2.5, mortgage: 3.9 }, { date: '2025-Q3', rate: 2.65, swap: 2.48, mortgage: 3.85 },
];

const HPI_HIST = [
  { date: '2020', ams: 100, rtd: 100, utr: 100, dhg: 100, ehv: 100 },
  { date: '2021', ams: 115, rtd: 118, utr: 117, dhg: 114, ehv: 116 },
  { date: '2022', ams: 120, rtd: 126, utr: 124, dhg: 119, ehv: 123 },
  { date: '2023', ams: 114, rtd: 119, utr: 117, dhg: 113, ehv: 117 },
  { date: '2024', ams: 119, rtd: 125, utr: 123, dhg: 118, ehv: 122 },
  { date: '2025', ams: 126, rtd: 133, utr: 131, dhg: 124, ehv: 130 },
];

const YIELDS = [
  { date: '2020', residential: 3.5, office: 4.5, retail: 5.5, logistics: 4.8 },
  { date: '2021', residential: 3.2, office: 4.2, retail: 5.8, logistics: 4.2 },
  { date: '2022', residential: 3.8, office: 5.0, retail: 6.2, logistics: 4.5 },
  { date: '2023', residential: 4.5, office: 6.0, retail: 6.8, logistics: 5.2 },
  { date: '2024', residential: 4.3, office: 5.9, retail: 6.5, logistics: 5.0 },
  { date: '2025', residential: 4.2, office: 5.8, retail: 6.3, logistics: 4.8 },
];

const INVEST_VOL = [
  { date: '2020', volume: 18.5 }, { date: '2021', volume: 22.8 }, { date: '2022', volume: 19.2 },
  { date: '2023', volume: 12.4 }, { date: '2024', volume: 14.8 }, { date: '2025', volume: 16.2 },
];

const CITY_RENT = [
  { city: 'Amsterdam', rent: 24.5, yoy: 5.2, vacancy: 1.8 },
  { city: 'Rotterdam', rent: 18.2, yoy: 6.1, vacancy: 2.4 },
  { city: 'Utrecht', rent: 19.8, yoy: 5.8, vacancy: 1.5 },
  { city: 'Den Haag', rent: 17.5, yoy: 4.2, vacancy: 2.1 },
  { city: 'Eindhoven', rent: 16.8, yoy: 7.3, vacancy: 1.9 },
];

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendingUp className="w-3 h-3 text-[#2E6F57]" />;
  if (trend === 'down') return <TrendingDown className="w-3 h-3 text-[#8A3C3C]" />;
  return <ArrowRight className="w-3 h-3 text-[#8C877F]" />;
}

export default function MacroIntelligence() {
  const navigate = useNavigate();

  // Sentiment score
  const sentiment = useMemo(() => {
    const rateScore = MACRO.ecbRate.v < 3 ? 18 : MACRO.ecbRate.v < 4 ? 12 : 5;
    const yieldSpread = (MACRO.yieldRes.v - MACRO.swap10y.v) * 10;
    const txScore = MACRO.txVolume.v > 0 ? 15 : MACRO.txVolume.v > -10 ? 10 : 5;
    const priceScore = MACRO.hpi.v > 5 ? 15 : MACRO.hpi.v > 0 ? 10 : 3;
    const inflScore = MACRO.inflation.v < 3 ? 12 : MACRO.inflation.v < 4 ? 8 : 3;
    const gdpScore = MACRO.gdp.v > 1.5 ? 12 : MACRO.gdp.v > 0 ? 8 : 3;
    const vacScore = 10;
    return Math.min(100, Math.round(rateScore + yieldSpread + txScore + priceScore + inflScore + gdpScore + vacScore));
  }, []);

  const sentimentLabel = sentiment >= 85 ? 'Strong' : sentiment >= 70 ? 'Constructive' : sentiment >= 50 ? 'Neutral' : sentiment >= 30 ? 'Risky' : 'Bearish';
  const sentimentColor = sentiment >= 70 ? '#2E6F57' : sentiment >= 50 ? '#A67C52' : '#8A3C3C';

  // Strategic signals
  const signals = [
    { label: 'Refinance Window', active: MACRO.ecbRate.trend === 'down', color: '#2E6F57' },
    { label: 'Acquisition Window', active: MACRO.yieldRes.v - MACRO.swap10y.v > 1.5, color: '#2E6F57' },
    { label: 'Yield Compression', active: MACRO.yieldRes.trend === 'down', color: '#A67C52' },
    { label: 'Liquidity Risk', active: MACRO.txVolume.v < -15, color: '#8A3C3C' },
  ];

  return (
    <div className="space-y-7 animate-fade-in" data-testid="macro-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Macro Intelligence — Netherlands</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Real Estate Market</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/scenarios')} className="flex items-center gap-1.5 px-3 py-2 bg-[#1F3A34] text-white rounded-lg text-[11px] font-sans font-medium hover:bg-[#285047]">Apply to Stress Test</button>
          <button onClick={() => { const csvData = Object.entries(MACRO).map(([k, v]) => `${k},${v.v},${v.prev},${v.avg12},${v.src}`).join('\n'); const blob = new Blob([`metric,current,previous,avg12m,source\n${csvData}`], {type: 'text/csv'}); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'macro_data.csv'; a.click(); }} className="flex items-center gap-1.5 px-3 py-2 border border-[#E2DDD3] text-[#5A5A5A] rounded-lg text-[11px] font-sans font-medium hover:border-[#1F3A34]/30"><Download className="w-3 h-3" /> CSV</button>
        </div>
      </div>

      {/* Sentiment + Signals */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 flex flex-col items-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <svg viewBox="0 0 100 100" className="w-24 h-24">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#F0EDE8" strokeWidth="6" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={sentimentColor} strokeWidth="6"
              strokeDasharray={`${2*Math.PI*42}`} strokeDashoffset={`${2*Math.PI*42*(1-sentiment/100)}`}
              strokeLinecap="round" transform="rotate(-90 50 50)" />
            <text x="50" y="48" textAnchor="middle" fill="#1C1C1C" fontSize="20" fontWeight="600" fontFamily="Inter">{sentiment}</text>
            <text x="50" y="62" textAnchor="middle" fill="#8C877F" fontSize="8">/100</text>
          </svg>
          <p className="text-[14px] font-sans font-semibold mt-2" style={{ color: sentimentColor }}>{sentimentLabel}</p>
          <p className="text-[10px] text-[#8C877F]">Macro Sentiment Index</p>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {signals.map(s => (
            <div key={s.label} className="bg-white border border-[#E2DDD3] rounded-xl p-4 flex items-center gap-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <div className={`w-3 h-3 rounded-full ${s.active ? '' : 'opacity-30'}`} style={{ backgroundColor: s.color }} />
              <div>
                <p className="text-[12px] text-[#1C1C1C] font-sans font-medium">{s.label}</p>
                <p className="text-[10px] font-sans font-medium" style={{ color: s.active ? s.color : '#8C877F' }}>{s.active ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Executive Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: 'ECB Rate', ...MACRO.ecbRate, suffix: '%', inv: true },
          { l: '10Y Swap', ...MACRO.swap10y, suffix: '%', inv: true },
          { l: 'Mortgage Avg', ...MACRO.mortgage, suffix: '%', inv: true },
          { l: 'House Price YoY', ...MACRO.hpi, suffix: '%' },
          { l: 'Rent Growth YoY', ...MACRO.rentGrowth, suffix: '%' },
          { l: 'Tx Volume YoY', ...MACRO.txVolume, suffix: '%' },
          { l: 'Yield Residential', ...MACRO.yieldRes, suffix: '%' },
          { l: 'Yield Office', ...MACRO.yieldOff, suffix: '%' },
          { l: 'Inflation', ...MACRO.inflation, suffix: '%', inv: true },
          { l: 'GDP Growth', ...MACRO.gdp, suffix: '%' },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] text-[#8C877F] font-sans font-medium">{k.l}</p>
              <TrendIcon trend={k.trend} />
            </div>
            <p className="text-[18px] font-sans font-semibold text-[#1C1C1C] leading-none">{k.v > 0 && k.l.includes('YoY') ? '+' : ''}{k.v}{k.suffix}</p>
            <div className="flex gap-2 mt-1">
              <span className="text-[9px] text-[#8C877F]">prev: {k.prev}{k.suffix}</span>
              <span className="text-[9px] text-[#8C877F]">12M: {k.avg12}{k.suffix}</span>
            </div>
            <p className="text-[8px] text-[#D9D3C8] mt-0.5">{k.src}</p>
          </div>
        ))}
      </div>

      {/* Rate Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Interest Rates — 5 Year</p>
          <p className="text-[9px] text-[#D9D3C8] mb-3">Sources: ECB, Reuters, DNB</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={ECB_HIST}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} domain={[0, 5]} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={ct} />
              <Line type="monotone" dataKey="rate" stroke="#8A3C3C" strokeWidth={1.8} dot={false} name="ECB Rate" />
              <Line type="monotone" dataKey="swap" stroke="#1F3A34" strokeWidth={1.5} dot={false} name="10Y Swap" />
              <Line type="monotone" dataKey="mortgage" stroke="#9C9488" strokeWidth={1} dot={false} strokeDasharray="4 3" name="Mortgage" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Prime Yields by Segment</p>
          <p className="text-[9px] text-[#D9D3C8] mb-3">Sources: CBRE, JLL, MSCI</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={YIELDS}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} domain={[2, 8]} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={ct} />
              <Line type="monotone" dataKey="residential" stroke="#1F3A34" strokeWidth={1.8} dot={false} name="Residential" />
              <Line type="monotone" dataKey="office" stroke="#9C9488" strokeWidth={1.5} dot={false} name="Office" />
              <Line type="monotone" dataKey="retail" stroke="#A67C52" strokeWidth={1} dot={false} name="Retail" />
              <Line type="monotone" dataKey="logistics" stroke="#285047" strokeWidth={1} dot={false} strokeDasharray="4 3" name="Logistics" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Housing + Investment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">House Price Index by City (2020=100)</p>
          <p className="text-[9px] text-[#D9D3C8] mb-3">Source: CBS / Kadaster</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={HPI_HIST}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} domain={[95, 140]} />
              <Tooltip contentStyle={ct} />
              <Line type="monotone" dataKey="ams" stroke="#1F3A34" strokeWidth={1.8} dot={false} name="Amsterdam" />
              <Line type="monotone" dataKey="rtd" stroke="#285047" strokeWidth={1.2} dot={false} name="Rotterdam" />
              <Line type="monotone" dataKey="utr" stroke="#9C9488" strokeWidth={1.2} dot={false} name="Utrecht" />
              <Line type="monotone" dataKey="dhg" stroke="#A67C52" strokeWidth={1} dot={false} name="Den Haag" />
              <Line type="monotone" dataKey="ehv" stroke="#D9D3C8" strokeWidth={1} dot={false} name="Eindhoven" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Investment Volume NL (EUR bn)</p>
          <p className="text-[9px] text-[#D9D3C8] mb-3">Sources: CBRE, JLL, RCA</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={INVEST_VOL}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}bn`} />
              <Tooltip contentStyle={ct} formatter={v => [`€${v}bn`, 'Volume']} />
              <Bar dataKey="volume" fill="#1F3A34" barSize={32} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* City Heatmap */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">City Rental Market</p>
        <p className="text-[9px] text-[#D9D3C8] mb-3">Sources: CBS, Pararius, Funda</p>
        <div className="grid grid-cols-5 gap-3">
          {CITY_RENT.map(c => (
            <div key={c.city} className="bg-[#F4F1EB] rounded-xl p-4 text-center space-y-2">
              <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">{c.city}</p>
              <p className="text-[22px] font-sans font-semibold text-[#1F3A34]">€{c.rent}</p>
              <p className="text-[10px] text-[#8C877F]">/m²/month</p>
              <div className="flex justify-center gap-3 text-[10px]">
                <span className="text-[#2E6F57] font-semibold">+{c.yoy}% YoY</span>
                <span className="text-[#8C877F]">{c.vacancy}% vac</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Macro Economy */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: 'Inflation', v: '2.4%', trend: 'down', c: '#2E6F57', src: 'CBS' },
          { l: 'Unemployment', v: '3.6%', trend: 'stable', c: '#2E6F57', src: 'CBS' },
          { l: 'Consumer Confidence', v: '-12', trend: 'up', c: '#A67C52', src: 'CBS' },
          { l: 'GDP Growth', v: '+1.8%', trend: 'up', c: '#2E6F57', src: 'CPB' },
          { l: 'Housing Shortage', v: '390k', trend: 'stable', c: '#8A3C3C', src: 'ABF Research' },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-[#8C877F] font-sans font-medium">{k.l}</p>
              <TrendIcon trend={k.trend} />
            </div>
            <p className="text-[20px] font-sans font-semibold leading-none" style={{ color: k.c }}>{k.v}</p>
            <p className="text-[8px] text-[#D9D3C8] mt-1">{k.src}</p>
          </div>
        ))}
      </div>

      {/* AI Macro Advisory */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">AI Macro Analysis</p></div>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-5">
          Yield spread residential vs 10Y swap at <strong>{(MACRO.yieldRes.v - MACRO.swap10y.v).toFixed(0) * 100}bps</strong> — historically supportive for acquisitions.
        </p>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-5">
          ECB rate declining from {MACRO.ecbRate.avg12}% to {MACRO.ecbRate.v}% — <strong className="text-[#2E6F57]">refinance window open</strong>. Mortgage rates following at {MACRO.mortgage.v}%.
        </p>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-5">
          Transaction volumes {Math.abs(MACRO.txVolume.v)}% below average — <strong className="text-[#A67C52]">liquidity below normal</strong>. Selective buying opportunities emerging.
        </p>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-5">
          Rent growth +{MACRO.rentGrowth.v}% YoY with housing shortage at 390k units — <strong className="text-[#2E6F57]">structural demand supports rental income</strong>. Underwriting assumptions can be moderately positive.
        </p>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-5">
          Inflation cooling to {MACRO.inflation.v}% — ECB likely to continue easing. Cap rate compression probable in H2 2026.
        </p>
      </div>

      {/* Sources */}
      <div className="text-[10px] text-[#D9D3C8] font-sans space-y-0.5">
        <p className="font-medium text-[#8C877F]">Data Sources</p>
        <p>ECB — European Central Bank | CBS — Centraal Bureau voor de Statistiek | Kadaster — Land Registry</p>
        <p>DNB — De Nederlandsche Bank | CPB — Centraal Planbureau | CBRE / JLL — Commercial Real Estate Research</p>
        <p>Pararius / Funda — Rental & Housing Market | ABF Research — Housing Shortage | MSCI / RCA — Investment Analytics</p>
        <p className="text-[#D9D3C8] italic">Data reflects latest available figures as of Q4 2025 / Q1 2026. Static data for demonstration purposes.</p>
      </div>
    </div>
  );
}
